import LogRocket from "logrocket";

import {
  addDoc,
  collection,
  doc as single,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { type FirebaseApp } from "firebase/app";
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  type Auth,
  type User,
  type UserProfile,
} from "firebase/auth";

import { FirebaseApiErrorNoDocumentFound } from "./errors";
import { APPLICATION_STATUS } from "../../config/ApplicationConstants";
import {
  FIRESTORE_DOCUMENT_COLLECTION,
  FIRESTORE_INVITATION_CODE_COLLECTION,
  FIRESTORE_MESSAGE_COLLECTION,
  FIRESTORE_MIDDESK_RESPONSE_COLLECTION,
  FIRESTORE_VIDEOASK_RESPONSE_COLLECTION,
  FIRESTORE_WAITLIST_COLLECTION,
} from "../../config";
import { type RawDocument } from "../../utils/selectors";

/** December 31st, 2020 */
const OFFICIAL_RELEASE_DATE = 1609459200;

export enum CertificateFetchStatus {
  FETCHED = "FETCHED",
  NOT_FOUND = "NOT_FOUND",
  NOT_READY = "NOT_READY",
  NOT_AUTHORIZED = "NOT_AUTHORIZED",
  PENDING = "PENDING",
  UNKNOWN_FAILURE = "UNKNOWN",
}

type CertificateFetchFailures =
  | CertificateFetchStatus.NOT_FOUND
  | CertificateFetchStatus.NOT_READY
  | CertificateFetchStatus.NOT_AUTHORIZED
  | CertificateFetchStatus.UNKNOWN_FAILURE;

/** Error designed to encapsulate errors returned from the certificate query. */
export class CertificateError extends Error {
  failure: CertificateFetchFailures;

  constructor(failure: CertificateFetchFailures) {
    super("Failed to access certificate.");
    this.name = "CertificateError";
    this.failure = failure;
  }
}

/** Retrieve document via certificate number. */
async function getDocumentFromCertificateNumber(
  app: FirebaseApp,
  certificateNo: string
) {
  const db = getFirestore(app);
  const snapshot = await getDocs(
    query(
      collection(db, FIRESTORE_DOCUMENT_COLLECTION),
      where("certificateData.certificateNumber", "==", certificateNo),
      limit(1)
    )
  );
  if (snapshot.empty) {
    throw new CertificateError(CertificateFetchStatus.NOT_FOUND);
  }
  const doc = snapshot.docs[0].data() as RawDocument;
  if (doc.status !== APPLICATION_STATUS.ACCEPT) {
    throw new CertificateError(CertificateFetchStatus.NOT_READY);
  }
  return doc;
}

async function getUserDocument(app: FirebaseApp, userId: string) {
  const db = getFirestore(app);
  const snapshot = await getDocs(
    query(
      collection(db, FIRESTORE_DOCUMENT_COLLECTION),
      where("userId", "==", userId),
      limit(1)
    )
  );

  if (snapshot.empty) {
    throw new FirebaseApiErrorNoDocumentFound();
  }
  const doc = snapshot.docs[0];
  const data = doc.data() as RawDocument;
  return { id: doc.id, ...data };
}

async function addToWaitlist(app: FirebaseApp, data: RawDocument) {
  const db = getFirestore(app);
  const update = { ...data, createdAt: new Date().getTime() };

  const ref = await addDoc(
    collection(db, FIRESTORE_WAITLIST_COLLECTION),
    update
  );

  return { ...update, id: ref.id };
}

async function getInvitationCode(app: FirebaseApp, code: string) {
  const db = getFirestore(app);
  const ref = single(
    collection(db, FIRESTORE_INVITATION_CODE_COLLECTION),
    code
  );
  const doc = await getDoc(ref);
  return doc.data();
}

async function getDocument(app: FirebaseApp, code: string) {
  const db = getFirestore(app);
  const ref = single(collection(db, FIRESTORE_DOCUMENT_COLLECTION), code);
  const doc = await getDoc(ref);
  return doc.data();
}

async function sendMessage(
  app: FirebaseApp,
  userId: string,
  documentId: string,
  message: Record<string, unknown>
) {
  const db = getFirestore(app);
  const update = {
    ...message,
    createdAt: new Date().getTime(),
    documentId,
    userId,
  };

  const ref = await addDoc(
    collection(db, FIRESTORE_MESSAGE_COLLECTION),
    update
  );
  return { /** ...update, */ id: ref.id };
}

async function getApplicationMessages(app: FirebaseApp, documentId: string) {
  const db = getFirestore(app);
  const snapshot = await getDocs(
    query(
      collection(db, FIRESTORE_MESSAGE_COLLECTION),
      where("documentId", "==", documentId),
      orderBy("createdAt", "asc")
    )
  );

  return snapshot.docs.map((doc) => doc.data());
}

async function getUserMessages(app: FirebaseApp, documentId: string) {
  const db = getFirestore(app);
  const snapshot = await getDocs(
    query(
      collection(db, FIRESTORE_MESSAGE_COLLECTION),
      where("type", "in", ["MESSAGE_SENT", "MESSAGE_RECEIVED"]),
      where("documentId", "==", documentId)
    )
  );

  return snapshot.docs.map((doc) => doc.data());
}

/** Admin view. */
async function getAllDocuments(app: FirebaseApp) {
  const db = getFirestore(app);
  const snapshot = await getDocs(
    query(
      collection(db, FIRESTORE_DOCUMENT_COLLECTION),
      where("createdAt", ">=", OFFICIAL_RELEASE_DATE),
      orderBy("createdAt", "desc")
    )
  );

  return snapshot.docs.reduce(
    ([ids, docs], doc) => [
      // @ts-ignore - Casting breaks compilation? https://stackoverflow.com/questions/62079477/line-0-parsing-error-cannot-read-property-map-of-undefined#comment120198117_63660413
      [...ids, doc.id],
      { ...docs, [doc.id]: { id: doc.id, ...doc.data() } },
    ],
    [[], {}]
  );
}

async function getAllFilteredDocuments(app: FirebaseApp, dateBy: number) {
  const db = getFirestore(app);
  const snapshot = await getDocs(
    query(
      collection(db, FIRESTORE_DOCUMENT_COLLECTION),
      where("createdAt", ">=", dateBy),
      orderBy("createdAt", "desc")
    )
  );

  return snapshot.docs.reduce(
    ([ids, docs], doc) => [
      // @ts-ignore - Casting breaks compilation? https://stackoverflow.com/questions/62079477/line-0-parsing-error-cannot-read-property-map-of-undefined#comment120198117_63660413
      [...ids, doc.id],
      { ...docs, [doc.id]: { id: doc.id, ...doc.data() } },
    ],
    [[], {}]
  );
}

/** @todo refactor */
async function retryMiddeskRequest(
  app: FirebaseApp,
  documentId: string,
  retryRequest: Record<string, unknown>
) {
  const db = getFirestore(app);

  // requests should be well-formatted
  const businessName = retryRequest.businessName; // (name)
  const address_line1 = retryRequest.businessAddressLine1;
  const city = retryRequest.businessAddressCity;
  const state = retryRequest.businessAddressState;
  const postal_code = retryRequest.businessAddressPostalCode;
  const ein = retryRequest.businessEIN; // (tin)

  const isDev = process.env.REACT_APP_STAGE !== "prod" ? true : false;
  const middeskUrl = isDev
    ? "https://localhost:5000"
    : "https://api.byblack.us";

  const resp = await fetch(`${middeskUrl}/api/v2/business/middesk`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name: businessName,
      city,
      state,
      production: !isDev,
      zipCode: postal_code,
      street: address_line1,
      ein,
    }),
  });
  const data = await resp.json();
  const doc = single(collection(db, FIRESTORE_DOCUMENT_COLLECTION), documentId);

  try {
    await setDoc(
      doc,
      {
        middeskID: data.id,
        updatedAt: new Date().getTime(),
      },
      { merge: true }
    );
    console.debug("successfully updated the document:", documentId);
  } catch (err) {
    console.error("Failed to update document:", err);
  }
}

async function getUserMiddeskResponses(app: FirebaseApp, middeskID: string) {
  const db = getFirestore(app);
  const snapshot = await getDocs(
    query(
      collection(db, FIRESTORE_MIDDESK_RESPONSE_COLLECTION),
      where("account_id", "==", middeskID),
      limit(1) // There should only be one
    )
  );

  /**
   * doc.data() is never undefined for query doc snapshots
   * & b/c of the limit(1), we can resolve on this doc.data()
   */
  if (snapshot.empty) {
    throw new Error("No documents found in getUserMiddeskResponses.");
  }
  const doc = snapshot[0];
  const middesk = doc.data() as { middeskData: any };
  return {
    insights: JSON.parse(middesk.middeskData).object.review.tasks,
  };
}

export type FirebaseUser = User & UserProfile;

export function setLogRocketId(user: FirebaseUser) {
  LogRocket.identify(user.uid, {
    email: user.email as string,
    firstName: user.firstName as string,
    lastName: user.lastName as string,
  });
}

async function createDocument(app: FirebaseApp, data: RawDocument) {
  const db = getFirestore(app);
  const update = { ...data, createdAt: new Date().getTime() };
  const ref = await addDoc(
    collection(db, FIRESTORE_DOCUMENT_COLLECTION),
    update
  );
  return { ...update, id: ref.id };
}

async function updateDocument(
  app: FirebaseApp,
  documentId: string,
  data: RawDocument
) {
  const db = getFirestore(app);
  const update = { ...data, updatedAt: new Date().getTime() };
  const ref = single(collection(db, FIRESTORE_DOCUMENT_COLLECTION), documentId);
  try {
    await setDoc(ref, update, { merge: true });
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/** Non-exhaustive */
interface BaseVideoAskAnswer {
  answer_id: string;
  question_id: string;
  type: string;
  transcription: string;
}

/** Non-exhaustive */
interface VideoAskResponse {
  answer:
    | (BaseVideoAskAnswer & {
        type: "text";
        input_text: string;
      })
    | (BaseVideoAskAnswer & {
        type: "poll";
        poll_option_content: string;
      })
    | (BaseVideoAskAnswer & {
        type: "audio" | "video";
        media_url: string;
      });
  question: {
    transcription: string;
    question_id: string;
    metadata?: Record<string, unknown>;
  };
}

/** This whole thing is crazy... */
async function getUserVideoAskResponses(app: FirebaseApp, contactId: string) {
  const db = getFirestore(app);
  const snapshot = await getDocs(
    query(
      collection(db, FIRESTORE_VIDEOASK_RESPONSE_COLLECTION),
      where("contactId", "==", contactId),
      limit(1)
    )
  );

  if (snapshot.empty) {
    console.warn(
      `No videoask available for application with contact id! ${contactId}`
    );
    return;
  }

  const doc = snapshot.docs[0];
  const { answers: _answers, questions: _questions } = doc.data();
  const videoask = {
    contactId,
    answers: JSON.parse(_answers) as VideoAskResponse["answer"][],
    questions: JSON.parse(_questions) as VideoAskResponse["question"][],
  };

  const questions = videoask.questions.reduce((qs, q) => ({
    ...qs,
    [q.question_id]: q,
  }));
  const responses = videoask.answers.reduce((_all, answer) => {
    const matchedQuestion = questions[answer.question_id];
    // TODO: skip intro questions (dont push it)
    if (matchedQuestion.metadata.custom_type === "button") return _all;
    return { ..._all, answer, question: matchedQuestion };
  }, [] as VideoAskResponse[]);

  return {
    contactId: videoask.contactId,
    responses,
  };
}

/** Enhance account creation with display name. */
async function createUserWithNameEmailAndPassword(
  auth: Auth,
  email: string,
  password: string,
  firstName?: string,
  lastName?: string
) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  if (firstName || lastName) {
    const displayName = [firstName?.trim() ?? "", lastName?.trim() ?? ""].join(
      " "
    );
    await updateProfile(cred.user, { displayName });
    await cred.user.reload();
  }
  return cred;
}

/** Sign up authentication */
function makeAuthenticateWithProvider(
  provider: FacebookAuthProvider | GoogleAuthProvider
) {
  return async function authenticate(auth: Auth) {
    return signInWithPopup(auth, provider);
  };
}

/** Bind the app to every api request. */
export default function getApi(app: FirebaseApp) {
  const auth = getAuth(app);
  return {
    addToWaitlist: addToWaitlist.bind(undefined, app),
    createDocument: createDocument.bind(undefined, app),
    getAllDocuments: getAllDocuments.bind(undefined, app),
    getAllFilteredDocuments: getAllFilteredDocuments.bind(undefined, app),
    getApplicationMessages: getApplicationMessages.bind(undefined, app),
    getDocument: getDocument.bind(undefined, app),
    getDocumentFromCertificateNumber: getDocumentFromCertificateNumber.bind(
      undefined,
      app
    ),
    getInvitationCode: getInvitationCode.bind(undefined, app),
    getUserDocument: getUserDocument.bind(undefined, app),
    getUserMessages: getUserMessages.bind(undefined, app),
    getUserMiddeskResponses: getUserMiddeskResponses.bind(undefined, app),
    getUserVideoAskResponses: getUserVideoAskResponses.bind(undefined, app),
    retryMiddeskRequest: retryMiddeskRequest.bind(undefined, app),
    sendMessage: sendMessage.bind(undefined, app),
    updateDocument: updateDocument.bind(undefined, app),

    /** Auth */
    authenticateWithFacebook: makeAuthenticateWithProvider(
      new FacebookAuthProvider()
    ).bind(undefined, auth),
    authenticateWithGoogle: makeAuthenticateWithProvider(
      new GoogleAuthProvider()
    ).bind(undefined, auth),
    createUserWithEmailAndPassword: createUserWithNameEmailAndPassword.bind(
      undefined,
      auth
    ),
    sendPasswordResetEmail: sendPasswordResetEmail.bind(undefined, auth),
    signInWithEmailAndPassword: signInWithEmailAndPassword.bind(
      undefined,
      auth
    ),
  };
}

export type FirebaseApi = ReturnType<typeof getApi>;
